import { useTranslation } from 'react-i18next'

import { Card, Skeleton } from '@/design-system/primitives'
import { useAdminQuery } from '@/features/admin/api'
import { JuryPanel } from '@/features/admin/components/JuryPanel'
import type { Standings } from '@/features/admin/types'
import { cn } from '@/lib/cn'

import { useContestBoard } from '../api'
import { ContestPicker, SectionHead, text, useContestChoice } from './shared'

/**
 * Le jury d'un concours : qui a noté quel tour, puis les jurés eux-mêmes.
 *
 * La progression d'abord : avant de geler un tour, le MC doit savoir qu'un
 * juré n'a pas encore noté — et lequel, pour le relancer de vive voix. La
 * composition ensuite : un juré absent se remplace, un téléphone perdu se
 * règle en régénérant son lien. Le lien d'un juré est personnel : il ne
 * s'affiche qu'à la demande, juré par juré.
 */
export function JuryTab({
  eventId,
  can,
}: {
  eventId: string | undefined
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const board = useContestBoard(eventId, true)
  const contests = board.data?.contests ?? []
  const [contest, choose] = useContestChoice(contests)
  const canManage = can('competition.jury.manage') || can('competition.category.update')

  if (board.isPending) return <Skeleton className="h-40 w-full" />
  if (!contest) {
    return <p className="text-content-secondary">{t('conductor.cockpit.contest.none')}</p>
  }

  return (
    <div className="space-y-8">
      <ContestPicker contests={contests} value={contest.id} onChange={choose} />

      {can('voting.result.read') ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.jury.progress')}
            hint={t('conductor.cockpit.jury.progressHint')}
          />
          <JuryProgress key={contest.id} eventId={eventId} contestId={contest.id} />
        </section>
      ) : null}

      {canManage ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.jury.members')}
            hint={t('conductor.cockpit.jury.membersHint')}
          />
          <JuryPanel
            key={contest.id}
            eventId={eventId}
            competitionCategoryId={contest.id}
            title={text(contest.name_i18n, i18n.language)}
          />
        </section>
      ) : null}
    </div>
  )
}

/**
 * Tour par tour, chaque juré : combien de candidats il a notés sur combien.
 *
 * Lu dans le tableau privé (le même que celui des scores) et relu toutes les
 * 15 s : on regarde les cases se remplir pendant que le jury note. Aucune
 * note n'est montrée ici — seulement qui a fini.
 */
function JuryProgress({ eventId, contestId }: { eventId: string | undefined; contestId: string }) {
  const { t, i18n } = useTranslation()
  const query = useAdminQuery<Standings>(
    ['admin-standings', contestId, i18n.language],
    `/admin/events/${eventId}/competition/categories/${contestId}/standings?locale=${i18n.language}`,
    eventId,
    { refetchInterval: 15_000 },
  )
  const standings = query.data

  if (query.isPending) return <Skeleton className="h-24 w-full" />
  if (query.isError || !standings) {
    return <p className="text-sm text-state-danger">{t('state.errorTitle')}</p>
  }
  if (standings.jurors.length === 0) {
    return (
      <p className="text-sm text-state-warning">
        {t('admin.standings.noJury', { jury: standings.jury_weight })}
      </p>
    )
  }
  if (standings.round_details.length === 0) {
    return <p className="text-sm text-content-secondary">{t('conductor.cockpit.jury.noRound')}</p>
  }

  return (
    <ul className="space-y-3">
      {standings.round_details.map((round, index) => {
        // Les candidats de ce tour : ceux qui y ont concouru.
        const competing = standings.rows.filter((row) => row.per_round[index]?.competed)
        const complete = round.jury_marks_given >= round.jury_marks_expected
        return (
          <li key={round.vote_category_id}>
            <Card className="p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="min-w-0 break-words font-display text-heading-sm text-content-primary">
                  {round.round_name}
                </h3>
                <span
                  className={cn(
                    'shrink-0 font-mono text-caption tabular-nums',
                    complete ? 'text-state-success' : 'text-state-warning',
                  )}
                >
                  {t('conductor.cockpit.jury.marks', {
                    given: round.jury_marks_given,
                    expected: round.jury_marks_expected,
                  })}
                </span>
              </div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {standings.jurors
                  .filter((juror) => !juror.revoked)
                  .map((juror) => {
                    const given = competing.filter((row) =>
                      row.per_round[index]?.jury_marks.some(
                        (mark) => mark.juror_id === juror.id && mark.score !== null,
                      ),
                    ).length
                    const done = competing.length > 0 && given >= competing.length
                    return (
                      <li
                        key={juror.id}
                        className={cn(
                          'max-w-full break-words rounded-2xl border px-3 py-1 text-caption',
                          done
                            ? 'border-state-success/40 text-state-success'
                            : 'border-state-warning/40 text-state-warning',
                        )}
                      >
                        {juror.name} · {given}/{competing.length}
                      </li>
                    )
                  })}
              </ul>
            </Card>
          </li>
        )
      })}
    </ul>
  )
}
